import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth'
import { useLang } from '../i18n'

interface Preset {
  id: string
  name: string
  included_task_ids: string[]
}

export function QuarterlyReportPage() {
  const { t } = useLang()
  const { accessToken, user } = useAuth()

  const [years, setYears] = useState<number[]>([])
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null)
  const [includeCustomRanking, setIncludeCustomRanking] = useState(false)
  const [presets, setPresets] = useState<Preset[]>([])
  const [selectedPresetId, setSelectedPresetId] = useState<string>('')
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 4000)
  }, [])

  // Fetch available years
  useEffect(() => {
    if (!accessToken) return
    fetch('/api/reports/available-years', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.years) {
          setYears(data.years)
          if (data.years.length > 0) {
            setSelectedYear(data.years[data.years.length - 1])
          }
        }
      })
      .catch(() => {})
  }, [accessToken])

  // Fetch leaderboard presets (for custom ranking)
  useEffect(() => {
    if (!accessToken) return
    fetch('/api/leaderboard-presets', {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setPresets(data)
        else if (Array.isArray(data.data)) setPresets(data.data)
      })
      .catch(() => {})
  }, [accessToken])

  // Default quarter to current
  useEffect(() => {
    if (selectedQuarter === null) {
      const now = new Date()
      setSelectedQuarter(Math.floor(now.getMonth() / 3) + 1)
    }
  }, [selectedQuarter])

  const handleGenerate = useCallback(async () => {
    if (!selectedYear || !selectedQuarter) {
      setError(t('qrSelectYear'))
      return
    }
    setError(null)
    setGenerating(true)

    try {
      const body: Record<string, unknown> = {
        year: selectedYear,
        quarter: selectedQuarter,
        include_custom_ranking: includeCustomRanking,
      }
      if (includeCustomRanking && selectedPresetId) {
        body.preset_id = selectedPresetId
      }

      const resp = await fetch('/api/reports/quarterly', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })

      if (resp.status === 409) {
        const data = await resp.json()
        showToast(data?.error?.message || t('qrEmptyQuarter'))
        return
      }

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        setError(data?.error?.message || `Error ${resp.status}`)
        return
      }

      // Download the file
      const blob = await resp.blob()
      const disposition = resp.headers.get('Content-Disposition') || ''
      const nameMatch = disposition.match(/filename="([^"]+)"/)
      const filename = nameMatch
        ? nameMatch[1]
        : `CSR_Workload_Analysis_${selectedYear}_Q${selectedQuarter}.xlsx`

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      setError('网络错误，请重试')
    } finally {
      setGenerating(false)
    }
  }, [
    selectedYear,
    selectedQuarter,
    includeCustomRanking,
    selectedPresetId,
    accessToken,
    t,
    showToast,
  ])

  if (user?.role === 'member') {
    return (
      <div style={{ padding: '24px' }}>
        <h2>{t('navQuarterlyReport')}</h2>
        <p style={{ color: 'var(--text-muted)' }}>{t('qrForbidden')}</p>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: 600 }}>
      <h2>{t('qrTitle')}</h2>

      {/* Toast */}
      {toast && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 16px',
            marginBottom: 16,
            color: 'var(--warning)',
          }}
        >
          {toast}
        </div>
      )}

      {/* Year select */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
          {t('qrYear')}
        </label>
        <select
          value={selectedYear ?? ''}
          onChange={(e) =>
            setSelectedYear(e.target.value ? parseInt(e.target.value) : null)
          }
          style={{
            padding: '6px 10px',
            borderRadius: 4,
            border: '1px solid var(--border)',
            minWidth: 120,
            fontSize: 14,
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
          }}
        >
          <option value="">{t('qrSelectYear')}</option>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {/* Quarter select */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}>
          {t('qrQuarter')}
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[1, 2, 3, 4].map((q) => (
            <button
              key={q}
              onClick={() => setSelectedQuarter(q)}
              style={{
                padding: '6px 18px',
                borderRadius: 4,
                border: `2px solid ${selectedQuarter === q ? 'var(--accent)' : 'var(--border)'}`,
                background: selectedQuarter === q ? 'var(--accent)' : 'var(--bg-surface)',
                color: selectedQuarter === q ? 'var(--accent-contrast)' : 'var(--text-primary)',
                cursor: 'pointer',
                fontWeight: selectedQuarter === q ? 700 : 400,
                fontSize: 14,
              }}
            >
              Q{q}
            </button>
          ))}
        </div>
      </div>

      {/* Custom ranking option */}
      {(
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeCustomRanking}
              onChange={(e) => setIncludeCustomRanking(e.target.checked)}
            />
            <span>{t('qrCustomRanking')}</span>
          </label>

          {includeCustomRanking && (
            <div style={{ marginTop: 8, marginLeft: 24 }}>
              <select
                value={selectedPresetId}
                onChange={(e) => setSelectedPresetId(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 4,
                  border: '1px solid var(--border)',
                  minWidth: 200,
                  fontSize: 14,
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="">{t('qrNoPreset')}</option>
                {presets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {presets.length === 0 && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {t('qrNoPreset')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
            borderRadius: 4,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={generating || !selectedYear || !selectedQuarter}
        style={{
          padding: '8px 24px',
          background: generating || !selectedYear || !selectedQuarter ? 'var(--text-muted)' : 'var(--accent)',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: generating || !selectedYear || !selectedQuarter ? 'not-allowed' : 'pointer',
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        {generating ? t('qrGenerating') : t('qrGenerate')}
      </button>

      {/* Info note */}
      <p style={{ marginTop: 20, fontSize: 12, color: 'var(--text-muted)' }}>
        {selectedYear && selectedQuarter
          ? `${selectedYear} Q${selectedQuarter}`
          : ''}
      </p>
    </div>
  )
}
